import { useEffect } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import EmptyState from '@atlaskit/empty-state';

import Loading from 'src/components/loading';
import { Workspace } from 'src/components/types';
import { LoadingStatus } from 'src/constants/loading-status';
import { WorkspacePermission } from 'src/redux/workspaces/constants';
import urls from 'src/urls/jira';
import {
  publishUiEvent,
  publishScreenEvent,
} from 'src/utils/analytics/publish';

import { SCREEN_NAME } from './constants';
import emptyStateConnectImgUrl from './empty-state-connect.svg';
import messages from './empty-state.i18n';
import { TertiaryText, LearnMoreLink } from './empty-state.styled';

export type JiraPageEmptyStateConnectProps = {
  intl: InjectedIntl;
  workspace: Workspace;
  workspaceName: string;
  workspacePermission?: WorkspacePermission;
  workspacePermissionFetchedStatus: LoadingStatus;
};

const handleLearnMoreLinkClick = () => {
  publishUiEvent({
    action: 'clicked',
    actionSubject: 'link',
    actionSubjectId: 'learnMoreLinkButton',
    source: 'repositoryJiraScreen',
  });
};

const handleOpenWorkspaceSettingsButton = () => {
  publishUiEvent({
    action: 'clicked',
    actionSubject: 'button',
    actionSubjectId: 'openSettingsButton',
    source: 'repositoryJiraScreen',
  });
};

const renderPrimaryAction = (
  workspace: Workspace,
  workspaceName: string,
  workspacePermissionFetchedStatus: LoadingStatus,
  workspacePermission?: WorkspacePermission
  // eslint-disable-next-line consistent-return
) => {
  if (workspacePermissionFetchedStatus === LoadingStatus.Success) {
    const workspaceLink = (
      <a href={urls.ui.workspace(workspaceName)} target="_blank">
        {workspace.name}
      </a>
    );

    return (
      <div>
        <CustomThemeButton
          appearance="primary"
          target="_blank"
          href={urls.ui.settingsJiraIntegration(workspaceName)}
          onClick={handleOpenWorkspaceSettingsButton}
          isDisabled={workspacePermission !== WorkspacePermission.Owner}
        >
          <FormattedMessage {...messages.openSettings} />
        </CustomThemeButton>
        <TertiaryText>
          {workspacePermission !== WorkspacePermission.Owner && (
            <FormattedMessage
              {...messages.tertiaryTextConnectNonAdmin}
              values={{ workspaceLink }}
            />
          )}
        </TertiaryText>
      </div>
    );
  } else if (workspacePermissionFetchedStatus === LoadingStatus.Fetching) {
    return <Loading size="small" />;
  } else if (workspacePermissionFetchedStatus === LoadingStatus.Failed) {
    return null;
  }
};

const JiraPageEmptyStateConnect = (props: JiraPageEmptyStateConnectProps) => {
  const {
    intl,
    workspace,
    workspaceName,
    workspacePermission,
    workspacePermissionFetchedStatus,
  } = props;

  useEffect(() => {
    publishScreenEvent(SCREEN_NAME, { variation: 'emptyStateConnect' });
  }, []);

  return (
    <EmptyState
      header={intl.formatMessage(messages.emptyStateConnectHeader)}
      // @ts-ignore fix: EmptyState.description expects string
      description={
        <span>
          {intl.formatMessage(messages.emptyStateConnectDescription)}
          <LearnMoreLink
            target="_blank"
            href={urls.external.docsConnectJira}
            onClick={handleLearnMoreLinkClick}
          >
            <FormattedMessage {...messages.learnMoreLink} />
          </LearnMoreLink>
        </span>
      }
      imageUrl={emptyStateConnectImgUrl}
      maxImageHeight={213}
      maxImageWidth={246}
      primaryAction={renderPrimaryAction(
        workspace,
        workspaceName,
        workspacePermissionFetchedStatus,
        workspacePermission
      )}
    />
  );
};

export default injectIntl(JiraPageEmptyStateConnect);
